import {
  useCareTaskRules,
  useUpsertCareTaskRule,
} from "@/api/board/useBoard";
import { CareProtocolApi } from "@/api/careProtocol/CareProtocolApi";
import { useMyOrganizations } from "@/api/organizations/useMyOrganizations";
import {
  CATALOG_WRITE_DENIED_MESSAGE,
  canMaintainTherapyCatalog,
  catalogAreaDeniedMessage,
  catalogWriteErrorMessage,
} from "@/api/therapy/catalogAccess";
import type {
  MedicationRow,
  TherapyRating,
  TreatmentRow,
} from "@/api/therapy/TherapyApi";
import {
  useDeleteDiagnosisTherapyRating,
  useDiagnosisTherapyRatings,
  useMedications,
  useOpenCatalogReviews,
  useResolveCatalogReview,
  useSetMedicationActive,
  useSetTreatmentActive,
  useTreatments,
  useUpsertDiagnosisTherapyRating,
  useUpsertMedication,
  useUpsertTreatment,
} from "@/api/therapy/useTherapy";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { DenseRow } from "@/ui/components/DenseRow";
import { EmptyState } from "@/ui/components/EmptyState";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { FloatingLabelListPicker } from "@/ui/components/FloatingLabelListPicker";
import { HeaderAction } from "@/ui/components/HeaderAction";
import { Screen } from "@/ui/components/Screen";
import { SectionCard } from "@/ui/components/SectionCard";
import { SoftSection } from "@/ui/components/SoftSection";
import { StatusChip } from "@/ui/components/StatusChip";
import { WorkTabs } from "@/ui/components/WorkTabs";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

const RATING_OPTIONS = [
  { value: "recommended", label: "Empfohlen" },
  { value: "maybe", label: "Möglicherweise" },
  { value: "not_recommended", label: "Nicht empfohlen" },
  { value: "contraindicated", label: "Kontraindiziert" },
  { value: "unrated", label: "Ohne Bewertung" },
];

type Tab = "meds" | "treatments" | "reviews" | "ratings" | "standards";

/**
 * P5/P5.1 Medikus: Kataloge (inkl. Bearbeiten/Deaktivieren), Review, Bewertungen.
 */
export default function MedicusScreen() {
  const { theme } = useTheme();
  const { organizations, memberships, loading: orgsLoading } = useMyOrganizations();
  const orgId = useMemo(() => organizations[0]?.id ?? null, [organizations]);
  const membership = useMemo(
    () => memberships.find((m) => m.organization_id === orgId && m.status === "active"),
    [memberships, orgId]
  );
  const catalogAccessKnown = !orgsLoading && !!membership;
  const canMaintainCatalog =
    catalogAccessKnown && canMaintainTherapyCatalog(membership.roles);

  const { medications, refetch: refetchMeds } = useMedications(orgId ?? undefined, {
    includeInactive: true,
  });
  const { treatments, refetch: refetchTrt } = useTreatments(orgId ?? undefined, {
    includeInactive: true,
  });
  const { reviews, refetch: refetchReviews } = useOpenCatalogReviews(orgId ?? undefined);
  const { ratings, refetch: refetchRatings } = useDiagnosisTherapyRatings(
    orgId ?? undefined
  );
  const upsertMed = useUpsertMedication(orgId ?? undefined);
  const upsertTrt = useUpsertTreatment(orgId ?? undefined);
  const setMedActive = useSetMedicationActive(orgId ?? undefined);
  const setTrtActive = useSetTreatmentActive(orgId ?? undefined);
  const resolveReview = useResolveCatalogReview(orgId ?? undefined);
  const upsertRating = useUpsertDiagnosisTherapyRating(orgId ?? undefined);
  const deleteRating = useDeleteDiagnosisTherapyRating(orgId ?? undefined);
  const { rules, refetch: refetchRules } = useCareTaskRules(orgId ?? undefined);
  const upsertRule = useUpsertCareTaskRule(orgId ?? undefined);
  const [checkHours, setCheckHours] = useState("24");
  const [weightHours, setWeightHours] = useState("48");

  useEffect(() => {
    const check = rules.find((r) => r.kind === "daily_check");
    const weight = rules.find((r) => r.kind === "weight");
    if (check) setCheckHours(String(check.interval_hours));
    if (weight) setWeightHours(String(weight.interval_hours));
  }, [rules]);

  const diagnosesQ = useQuery({
    queryKey: ["care", "diagnoses", orgId ?? ""],
    queryFn: () => CareProtocolApi.listDiagnoses(orgId!),
    enabled: !!orgId,
  });

  const [tab, setTab] = useState<Tab>("meds");
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [substance, setSubstance] = useState("");
  const [notes, setNotes] = useState("");
  const [warnings, setWarnings] = useState("");
  const [source, setSource] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [acceptMedId, setAcceptMedId] = useState<string | null>(null);
  const [acceptTrtId, setAcceptTrtId] = useState<string | null>(null);
  const [expandedReviewId, setExpandedReviewId] = useState<string | null>(null);

  const [ratingDiagnosisId, setRatingDiagnosisId] = useState<string | null>(null);
  const [ratingTargetKind, setRatingTargetKind] = useState<"medication" | "treatment">(
    "medication"
  );
  const [ratingMedId, setRatingMedId] = useState<string | null>(null);
  const [ratingTrtId, setRatingTrtId] = useState<string | null>(null);
  const [ratingValue, setRatingValue] = useState<string>("unrated");
  const [ratingNotes, setRatingNotes] = useState("");

  const activeMeds = useMemo(
    () => medications.filter((m) => m.is_active),
    [medications]
  );
  const activeTrts = useMemo(
    () => treatments.filter((t) => t.is_active),
    [treatments]
  );

  const medOptions = useMemo(
    () => activeMeds.map((m) => ({ value: m.id, label: m.name })),
    [activeMeds]
  );
  const trtOptions = useMemo(
    () => activeTrts.map((t) => ({ value: t.id, label: t.name })),
    [activeTrts]
  );
  const diagnosisOptions = useMemo(
    () => (diagnosesQ.data ?? []).map((d) => ({ value: d.id, label: d.label })),
    [diagnosesQ.data]
  );
  const diagnosisLabel = useMemo(() => {
    const map = new Map((diagnosesQ.data ?? []).map((d) => [d.id, d.label]));
    return (id: string) => map.get(id) ?? id;
  }, [diagnosesQ.data]);
  const medLabel = useMemo(() => {
    const map = new Map(medications.map((m) => [m.id, m.name]));
    return (id: string | null) => (id ? map.get(id) ?? id : "–");
  }, [medications]);
  const trtLabel = useMemo(() => {
    const map = new Map(treatments.map((t) => [t.id, t.name]));
    return (id: string | null) => (id ? map.get(id) ?? id : "–");
  }, [treatments]);

  const resetForm = () => {
    setEditId(null);
    setName("");
    setSubstance("");
    setNotes("");
    setWarnings("");
    setSource("");
  };

  const loadMed = (row: MedicationRow) => {
    setEditId(row.id);
    setName(row.name);
    setSubstance(row.substance ?? "");
    setNotes(row.dosing_notes ?? "");
    setWarnings(row.warnings ?? "");
    setSource(row.source_text ?? "");
    setTab("meds");
  };

  const loadTrt = (row: TreatmentRow) => {
    setEditId(row.id);
    setName(row.name);
    setSubstance("");
    setNotes(row.description ?? "");
    setWarnings(row.warnings ?? "");
    setSource(row.source_text ?? "");
    setTab("treatments");
  };

  const onSaveCatalog = async () => {
    setMessage(null);
    if (!canMaintainCatalog) {
      setMessage(CATALOG_WRITE_DENIED_MESSAGE);
      return;
    }
    if (!orgId || !name.trim()) {
      setMessage("Name erforderlich.");
      return;
    }
    try {
      if (tab === "meds") {
        await upsertMed.mutateAsync({
          id: editId ?? undefined,
          organization_id: orgId,
          name: name.trim(),
          substance: substance.trim() || null,
          dosing_notes: notes.trim() || null,
          warnings: warnings.trim() || null,
          source_text: source.trim() || null,
          weight_based_rules: [],
          is_active: true,
        });
        await refetchMeds();
      } else if (tab === "treatments") {
        await upsertTrt.mutateAsync({
          id: editId ?? undefined,
          organization_id: orgId,
          name: name.trim(),
          description: notes.trim() || null,
          warnings: warnings.trim() || null,
          source_text: source.trim() || null,
          is_active: true,
        });
        await refetchTrt();
      }
      resetForm();
      setMessage(
        editId
          ? "Katalog aktualisiert (historische Protokolle unverändert)."
          : "Gespeichert."
      );
    } catch (e: unknown) {
      setMessage(catalogWriteErrorMessage(e, "Speichern fehlgeschlagen."));
    }
  };

  const onSaveRating = async () => {
    setMessage(null);
    if (!canMaintainCatalog) {
      setMessage(CATALOG_WRITE_DENIED_MESSAGE);
      return;
    }
    if (!orgId || !ratingDiagnosisId) {
      setMessage("Diagnose wählen.");
      return;
    }
    if (ratingTargetKind === "medication" && !ratingMedId) {
      setMessage("Medikament wählen.");
      return;
    }
    if (ratingTargetKind === "treatment" && !ratingTrtId) {
      setMessage("Behandlung wählen.");
      return;
    }
    try {
      await upsertRating.mutateAsync({
        organization_id: orgId,
        diagnosis_id: ratingDiagnosisId,
        medication_id: ratingTargetKind === "medication" ? ratingMedId : null,
        treatment_id: ratingTargetKind === "treatment" ? ratingTrtId : null,
        rating: ratingValue as TherapyRating,
        notes: ratingNotes.trim() || null,
      });
      setRatingNotes("");
      await refetchRatings();
      setMessage("Bewertung gespeichert.");
    } catch (e: unknown) {
      setMessage(catalogWriteErrorMessage(e, "Bewertung fehlgeschlagen."));
    }
  };

  const toggleCatalogActive = async (
    kind: "meds" | "treatments",
    row: MedicationRow | TreatmentRow
  ) => {
    if (!canMaintainCatalog) {
      setMessage(CATALOG_WRITE_DENIED_MESSAGE);
      return;
    }
    try {
      if (kind === "meds") {
        await setMedActive.mutateAsync({
          id: row.id,
          isActive: !row.is_active,
        });
        await refetchMeds();
      } else {
        await setTrtActive.mutateAsync({
          id: row.id,
          isActive: !row.is_active,
        });
        await refetchTrt();
      }
      setMessage(
        row.is_active
          ? "Deaktiviert (Protokoll-Snapshots bleiben)."
          : "Wieder aktiviert."
      );
    } catch (e: unknown) {
      setMessage(catalogWriteErrorMessage(e, "Speichern fehlgeschlagen."));
    }
  };

  const renderCatalogPanel = (kind: "meds" | "treatments") => {
    const rows = kind === "meds" ? medications : treatments;
    return (
      <View style={{ gap: theme.space.md }}>
        {canMaintainCatalog ? (
        <SectionCard
          title={
            editId
              ? kind === "meds"
                ? "Medikament bearbeiten"
                : "Behandlung bearbeiten"
              : kind === "meds"
                ? "Medikament anlegen"
                : "Behandlung anlegen"
          }
        >
          <View style={{ gap: theme.space.sm }}>
            <FloatingLabelInput label="Name" value={name} onChangeText={setName} />
            {kind === "meds" ? (
              <FloatingLabelInput
                label="Wirkstoff"
                value={substance}
                onChangeText={setSubstance}
              />
            ) : null}
            <FloatingLabelInput
              label={kind === "meds" ? "Dosierungshinweise" : "Beschreibung"}
              value={notes}
              onChangeText={setNotes}
            />
            <FloatingLabelInput
              label="Warnungen"
              value={warnings}
              onChangeText={setWarnings}
            />
            <FloatingLabelInput
              label="Quelle"
              value={source}
              onChangeText={setSource}
            />
            <View style={{ flexDirection: "row", gap: theme.space.sm }}>
              {editId ? (
                <AppButton variant="secondary" onPress={resetForm}>
                  Neu
                </AppButton>
              ) : null}
              <AppButton
                onPress={onSaveCatalog}
                disabled={upsertMed.isPending || upsertTrt.isPending}
              >
                {editId ? "Aktualisieren" : "Speichern"}
              </AppButton>
            </View>
          </View>
        </SectionCard>
        ) : catalogAccessKnown ? (
          <SectionCard
            title={kind === "meds" ? "Medikament anlegen" : "Behandlung anlegen"}
          >
            <Text
              style={{
                color: theme.color.text,
                fontSize: theme.fontSize.small,
              }}
            >
              {kind === "meds"
                ? catalogAreaDeniedMessage.medication
                : catalogAreaDeniedMessage.treatment}
            </Text>
          </SectionCard>
        ) : null}

        <SectionCard title={kind === "meds" ? "Medikamente" : "Behandlungen"}>
          {rows.length === 0 ? (
            <EmptyState
              title="Noch leer."
              hint={
                !catalogAccessKnown
                  ? undefined
                  : canMaintainCatalog
                    ? kind === "meds"
                      ? "Lege oben ein Medikament an."
                      : "Lege oben eine Behandlung an."
                    : kind === "meds"
                      ? catalogAreaDeniedMessage.medication
                      : catalogAreaDeniedMessage.treatment
              }
            />
          ) : (
            <View style={{ marginHorizontal: -theme.space.md }}>
              {rows.map((row) => {
                const meta =
                  kind === "meds"
                    ? (row as MedicationRow).substance ||
                      (row as MedicationRow).dosing_notes ||
                      undefined
                    : (row as TreatmentRow).description || undefined;
                return (
                  <DenseRow
                    key={row.id}
                    title={row.name}
                    meta={meta}
                    onPress={
                      canMaintainCatalog
                        ? () =>
                            kind === "meds"
                              ? loadMed(row as MedicationRow)
                              : loadTrt(row as TreatmentRow)
                        : undefined
                    }
                    trailing={
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: theme.space.sm,
                        }}
                      >
                        <StatusChip
                          label={row.is_active ? "Aktiv" : "Inaktiv"}
                          tone={row.is_active ? "success" : "neutral"}
                          size="sm"
                          active={row.is_active}
                        />
                        {canMaintainCatalog ? (
                        <Pressable
                          onPress={() => toggleCatalogActive(kind, row)}
                          hitSlop={8}
                        >
                          <Text
                            style={{
                              color: theme.color.primary,
                              fontSize: theme.fontSize.tiny,
                              fontWeight: "700",
                            }}
                          >
                            {row.is_active ? "Deaktivieren" : "Aktivieren"}
                          </Text>
                        </Pressable>
                        ) : null}
                      </View>
                    }
                  />
                );
              })}
            </View>
          )}
        </SectionCard>
      </View>
    );
  };

  const reviewsPanel = (
    <SoftSection title="Offene Review-Items">
      {reviews.length === 0 ? (
        catalogAccessKnown && !canMaintainCatalog ? (
          <EmptyState title={catalogAreaDeniedMessage.review} />
        ) : (
          <EmptyState title="Keine offenen Items." />
        )
      ) : (
        <View style={{ gap: theme.space.sm }}>
          {reviews.map((r) => {
            const kindLabel = r.kind === "medication" ? "Medikament" : "Behandlung";
            const expanded = expandedReviewId === r.id;
            return (
              <View key={r.id} style={{ gap: theme.space.sm }}>
                <DenseRow
                  title={`${kindLabel}: ${r.free_name}`}
                  meta="Protokoll unverändert · Zuordnung nur in der Resolution"
                  emphasized={expanded}
                  onPress={() =>
                    setExpandedReviewId((id) => (id === r.id ? null : r.id))
                  }
                  trailing={
                    <StatusChip
                      label={expanded ? "Offen" : "Prüfen"}
                      tone="warning"
                      size="sm"
                    />
                  }
                />
                {expanded && catalogAccessKnown && !canMaintainCatalog ? (
                  <Text
                    style={{
                      color: theme.color.text,
                      fontSize: theme.fontSize.small,
                      paddingHorizontal: theme.space.xs,
                    }}
                  >
                    {catalogAreaDeniedMessage.review}
                  </Text>
                ) : null}
                {expanded && canMaintainCatalog ? (
                  <View style={{ gap: theme.space.sm, paddingHorizontal: theme.space.xs }}>
                    <Text
                      style={{
                        color: theme.color.muted,
                        fontSize: theme.fontSize.tiny,
                      }}
                    >
                      Protokolleintrag bleibt unverändert; Zuordnung nur in der
                      Review-Resolution.
                    </Text>
                    {r.kind === "medication" ? (
                      <FloatingLabelListPicker
                        label="Katalog-Medikament"
                        options={medOptions}
                        value={acceptMedId}
                        onChange={setAcceptMedId}
                        placeholder="Zuordnen…"
                      />
                    ) : (
                      <FloatingLabelListPicker
                        label="Katalog-Behandlung"
                        options={trtOptions}
                        value={acceptTrtId}
                        onChange={setAcceptTrtId}
                        placeholder="Zuordnen…"
                      />
                    )}
                    <View style={{ flexDirection: "row", gap: theme.space.sm }}>
                      <AppButton
                        onPress={async () => {
                          try {
                            await resolveReview.mutateAsync({
                              reviewId: r.id,
                              status: "accepted",
                              medicationId:
                                r.kind === "medication" ? acceptMedId : null,
                              treatmentId:
                                r.kind === "treatment" ? acceptTrtId : null,
                            });
                            await refetchReviews();
                            setExpandedReviewId(null);
                            setMessage("Akzeptiert (Protokoll unverändert).");
                          } catch (e: unknown) {
                            setMessage(catalogWriteErrorMessage(e, "Fehler"));
                          }
                        }}
                        disabled={resolveReview.isPending}
                      >
                        Akzeptieren
                      </AppButton>
                      <AppButton
                        variant="outline"
                        onPress={async () => {
                          try {
                            await resolveReview.mutateAsync({
                              reviewId: r.id,
                              status: "rejected",
                            });
                            await refetchReviews();
                            setExpandedReviewId(null);
                          } catch (e: unknown) {
                            setMessage(catalogWriteErrorMessage(e, "Fehler"));
                          }
                        }}
                      >
                        Ablehnen
                      </AppButton>
                    </View>
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
      )}
    </SoftSection>
  );

  const standardsPanel = (
    <SectionCard title="Organisationsstandards (Pflegeintervalle)">
      <Text
        style={{
          color: theme.color.muted,
          fontSize: theme.fontSize.small,
          marginBottom: theme.space.sm,
        }}
      >
        Standardintervalle für allgemeine Kontrolle und Gewicht. Keine
        Medikamentenrhythmik – die kommt aus Behandlungsplänen (P5).
      </Text>
      <View style={{ gap: theme.space.sm }}>
        {canMaintainCatalog ? (
          <>
        <FloatingLabelInput
          label="Kontrolle alle X Stunden"
          value={checkHours}
          onChangeText={setCheckHours}
        />
        <FloatingLabelInput
          label="Gewicht alle X Stunden"
          value={weightHours}
          onChangeText={setWeightHours}
        />
        <AppButton
          onPress={async () => {
            if (!canMaintainCatalog) {
              setMessage(CATALOG_WRITE_DENIED_MESSAGE);
              return;
            }
            if (!orgId) return;
            const check = Number(checkHours.replace(",", "."));
            const weight = Number(weightHours.replace(",", "."));
            if (
              !Number.isFinite(check) ||
              check <= 0 ||
              !Number.isFinite(weight) ||
              weight <= 0
            ) {
              setMessage("Intervalle müssen > 0 sein.");
              return;
            }
            try {
              const existingCheck = rules.find((r) => r.kind === "daily_check");
              const existingWeight = rules.find((r) => r.kind === "weight");
              await upsertRule.mutateAsync({
                id: existingCheck?.id,
                organization_id: orgId,
                kind: "daily_check",
                interval_hours: check,
                is_active: true,
              });
              await upsertRule.mutateAsync({
                id: existingWeight?.id,
                organization_id: orgId,
                kind: "weight",
                interval_hours: weight,
                is_active: true,
              });
              await refetchRules();
              setMessage("Standards gespeichert.");
            } catch (e: unknown) {
              setMessage(catalogWriteErrorMessage(e, "Speichern fehlgeschlagen."));
            }
          }}
        >
          Standards speichern
        </AppButton>
          </>
        ) : catalogAccessKnown ? (
          <Text
            style={{ color: theme.color.text, fontSize: theme.fontSize.small }}
          >
            {catalogAreaDeniedMessage.standards}
          </Text>
        ) : null}
        {rules.map((r) => (
          <Text
            key={r.id}
            style={{ color: theme.color.muted, fontSize: theme.fontSize.tiny }}
          >
            {r.kind === "daily_check" ? "Kontrolle" : "Gewicht"}: alle{" "}
            {r.interval_hours} h
          </Text>
        ))}
      </View>
    </SectionCard>
  );

  const ratingsPanel = (
    <View style={{ gap: theme.space.md }}>
      {canMaintainCatalog ? (
      <SectionCard title="Diagnose–Therapie-Bewertung">
        <View style={{ gap: theme.space.sm }}>
          <FloatingLabelListPicker
            label="Diagnose"
            options={diagnosisOptions}
            value={ratingDiagnosisId}
            onChange={setRatingDiagnosisId}
            placeholder="Diagnose…"
          />
          <View style={{ flexDirection: "row", gap: theme.space.xs }}>
            <AppButton
              variant={ratingTargetKind === "medication" ? "primary" : "outline"}
              onPress={() => setRatingTargetKind("medication")}
            >
              Medikament
            </AppButton>
            <AppButton
              variant={ratingTargetKind === "treatment" ? "primary" : "outline"}
              onPress={() => setRatingTargetKind("treatment")}
            >
              Behandlung
            </AppButton>
          </View>
          {ratingTargetKind === "medication" ? (
            <FloatingLabelListPicker
              label="Medikament"
              options={medOptions}
              value={ratingMedId}
              onChange={setRatingMedId}
            />
          ) : (
            <FloatingLabelListPicker
              label="Behandlung"
              options={trtOptions}
              value={ratingTrtId}
              onChange={setRatingTrtId}
            />
          )}
          <FloatingLabelListPicker
            label="Bewertung"
            options={RATING_OPTIONS}
            value={ratingValue}
            onChange={(v) => setRatingValue(v ?? "unrated")}
          />
          <FloatingLabelInput
            label="Begründung / Quelle (optional)"
            value={ratingNotes}
            onChangeText={setRatingNotes}
          />
          <AppButton onPress={onSaveRating} disabled={upsertRating.isPending}>
            Bewertung speichern
          </AppButton>
        </View>
      </SectionCard>
      ) : catalogAccessKnown ? (
        <SectionCard title="Diagnose–Therapie-Bewertung">
          <Text style={{ color: theme.color.text, fontSize: theme.fontSize.small }}>
            {catalogAreaDeniedMessage.rating}
          </Text>
        </SectionCard>
      ) : null}
      <SoftSection title="Vorhandene Bewertungen">
        {ratings.length === 0 ? (
          <EmptyState title="Noch keine Bewertungen." />
        ) : (
          <View style={{ marginHorizontal: -theme.space.xs }}>
            {ratings.map((r) => {
              const ratingLabel =
                RATING_OPTIONS.find((o) => o.value === r.rating)?.label ??
                "Ohne Bewertung";
              return (
                <DenseRow
                  key={r.id}
                  title={`${diagnosisLabel(r.diagnosis_id)} · ${
                    r.medication_id
                      ? medLabel(r.medication_id)
                      : trtLabel(r.treatment_id)
                  }`}
                  meta={r.notes ? `${ratingLabel} · ${r.notes}` : ratingLabel}
                  trailing={
                    canMaintainCatalog ? (
                    <HeaderAction
                      label="Entfernen"
                      tone="danger"
                      onPress={async () => {
                        try {
                          await deleteRating.mutateAsync(r.id);
                          await refetchRatings();
                        } catch (e: unknown) {
                          setMessage(catalogWriteErrorMessage(e, "Entfernen fehlgeschlagen."));
                        }
                      }}
                    />
                    ) : null
                  }
                />
              );
            })}
          </View>
        )}
      </SoftSection>
    </View>
  );

  const reviewTabLabel =
    reviews.length > 0 ? `Review · ${reviews.length} offen` : "Review";

  return (
    <Screen
      title="Medikus"
      scroll
      contentStyle={{ paddingBottom: theme.space.xxl }}
    >
      <View style={{ gap: theme.space.md }}>
        <Text
          style={{
            color: theme.color.muted,
            fontSize: theme.fontSize.small,
          }}
        >
          Kataloge, Bewertungen und Review. Umbenennungen ändern historische
          Protokoll-Snapshots nicht.
        </Text>

        <WorkTabs
          activeId={tab}
          onSelect={(id) => {
            const next = id as Tab;
            if (next === "meds" || next === "treatments") resetForm();
            setTab(next);
          }}
          tabs={[
            { id: "meds", label: "Medikamente", panel: renderCatalogPanel("meds") },
            {
              id: "treatments",
              label: "Behandlungen",
              panel: renderCatalogPanel("treatments"),
            },
            { id: "standards", label: "Standards", panel: standardsPanel },
            { id: "ratings", label: "Bewertungen", panel: ratingsPanel },
            { id: "reviews", label: reviewTabLabel, panel: reviewsPanel },
          ]}
        />

        {message ? (
          <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
            {message}
          </Text>
        ) : null}
      </View>
    </Screen>
  );
}
